import React, { useState, useEffect, useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import BeSafeMultiSelect from './commons/multiselect/BeSafeMultiSelect';
import { getDashboardProfiles } from 'app/crud/profiles.crud';
import BeSafeContainer from './commons/BeSafeContainer';

const ProfileFilter = ({ values, ...props }) => {
  const [profiles, setProfiles] = useState(props.initialValues || []);
  const intl = useIntl();

  const formatGroupName = useCallback(
    (functionalityName) => {
      return intl.formatMessage({
        id: `ADMINISTRATION.${functionalityName.toUpperCase()}`,
      });
    },
    [intl]
  );

  const formatProfileNameWithFunctionality = useCallback((profile) => {
    return `${profile.name} (${profile.functionality_type.toUpperCase()})`;
  }, []);

  const transformProfiles = useCallback(
    (profiles) => {
      const groupedProfiles = profiles.reduce((acc, profile) => {
        const groupKey = profile.functionality_name;

        if (!acc[groupKey]) {
          acc[groupKey] = {
            name: groupKey,
            displayName: formatGroupName(
              profile.functionality_name,
              profile.functionality_type
            ),
            children: [],
          };
        }

        acc[groupKey].children.push({
          name: profile.id,
          displayName: formatProfileNameWithFunctionality(profile),
          children: [],
        });

        return acc;
      }, {});

      return Object.values(groupedProfiles);
    },
    [formatGroupName, formatProfileNameWithFunctionality]
  );

  const postProcessProfiles = useCallback((profiles) => {
    return profiles.map((group) => {
      const childrenFunctionalityTypes = group.children.map(
        (profile) => profile.functionality_type
      );

      if (childrenFunctionalityTypes.length > 1) return group;

      return {
        ...group,
        children: group.children.map((profile) => ({
          ...profile,
          displayName: profile.displayName.replace(/\s\([A-Z]+\)$/, ''),
        })),
      };
    });
  }, []);

  const fetchProfiles = useCallback(async () => {
    const response = await getDashboardProfiles().catch(() => undefined);
    if (!response) return;
    setProfiles(response.data?.profiles || []);
  }, []);

  useEffect(() => {
    fetchProfiles();
  }, [fetchProfiles]);

  const parsedProfiles = transformProfiles(profiles);
  const postProcessedProfiles = postProcessProfiles(parsedProfiles);

  const findProfileById = (profileId) => {
    const allProfiles = postProcessedProfiles.flatMap(
      (group) => group.children
    );
    return allProfiles.find((profile) => profile.name === profileId);
  };

  const formatProfileName = (profileId) => {
    const profile = findProfileById(profileId);
    return profile?.displayName;
  };

  return (
    <div className={`${props.className} align-items-center`}>
      <BeSafeContainer visible={!props.withoutTitle}>
        <span className="font-weight-bold mr-10">
          <FormattedMessage id="PROFILES.FILTER" />:
        </span>
      </BeSafeContainer>

      <BeSafeMultiSelect
        items={postProcessedProfiles}
        onChange={(items) => {
          const selectedProfiles = items.map((profileId) =>
            profiles.find((profile) => profile.id === profileId)
          );
          props.setProfileParams(selectedProfiles);
        }}
        formatName={formatProfileName}
        selectedItems={values?.map((profile) => profile.id) || []}
      />
    </div>
  );
};

export default ProfileFilter;
